// ============================================================================
// vpn/VpnConnections.tsx — режим «Подключения» (по умолчанию для ЛЮБОГО
// пользователя): только доступные ему подключения, большая кнопка
// «Подключиться»/«Отключиться», состояние, срок доступа; QR и файл настроек —
// в «Расширенных». Технические параметры профиля не показываются.
//
// SSoT — сервер: ListVpnProfiles для не-администратора отдаёт ТОЛЬКО профили,
// доступные по ролям; GetVpnConfig/Start/FinishVpnSession проверяют права.
// Живое обновление — шина vpn.profile.* / vpn.access.updated.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { useCenterStore } from '@/store/centerStore';
import { useAuthStore } from '@/store/authStore';
import { createLogger } from '@/lib/logger';
import { eventBusHub } from '@/lib/eventBusHub';
import { logSwallowed } from '@/lib/errors';
import { VpnConnectionCard } from './VpnConnectionCard';
import { VpnConnectPanel } from './VpnConnectPanel';
import { vpnConnectionLabel, type VpnConnectionProfile } from './vpnTypes';
import { vpnErrorMessage } from './vpnErrors';

const log = createLogger('VpnConnections');

/** Открытые сессии ЭТОЙ вкладки (переживают обновление страницы; журнал — на
 *  сервере). Ключ — база + пользователь: смена базы/аккаунта не показывает
 *  чужие подключения. */
const SESSION_MAP_PREFIX = 'hydra_vpn_sessions';

function sessionMapKey(baseSlug: string, userId: string): string {
  return `${SESSION_MAP_PREFIX}:${baseSlug || 'local'}:${userId || 'anonymous'}`;
}

function readSessions(key: string): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    for (const [code, id] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof id === 'string' && id) out[code] = id;
    }
    return out;
  } catch (e) {
    logSwallowed(e, 'components/vpn/VpnConnections.tsx:readSessions');
    return {};
  }
}

function writeSessions(key: string, map: Record<string, string>): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(map));
  } catch (e) {
    logSwallowed(e, 'components/vpn/VpnConnections.tsx:writeSessions');
  }
}

function clientInfo(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return ua ? `Веб-приложение · ${ua.slice(0, 120)}` : 'Веб-приложение';
}

interface VpnConnectionsProps {
  /** Есть ли доступ к VPN (админ / vpn_client / vpn_manage). */
  canUse: boolean;
  /** Управляющий VPN — подсказка, где создавать подключения. */
  canManage: boolean;
}

export function VpnConnections({ canUse, canManage }: VpnConnectionsProps) {
  const baseSlug = useCenterStore((s) => s.baseSlug || '');
  const userId = useAuthStore((s) => s.user?.id || '');
  const sessionKey = sessionMapKey(baseSlug, userId);
  const sessionKeyRef = useRef(sessionKey);

  const [profiles, setProfiles] = useState<VpnConnectionProfile[]>([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [sessions, setSessions] = useState<Record<string, string>>(() => readSessions(sessionKey));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busyCode, setBusyCode] = useState('');

  const showSuccess = (m: string) => useToastStore.getState().showSuccess(m);
  const showError = (m: string) => useToastStore.getState().showError(m);

  // Смена базы/пользователя — берём сессии из хранилища нового ключа
  // (старые не записываем в новый ключ: сначала чтение, запись — следующим рендером).
  useEffect(() => {
    if (sessionKeyRef.current !== sessionKey) {
      sessionKeyRef.current = sessionKey;
      setSessions(readSessions(sessionKey));
      return;
    }
    writeSessions(sessionKey, sessions);
  }, [sessionKey, sessions]);

  const loadProfiles = useCallback(async (silent = false) => {
    if (!canUse) {
      setProfiles([]);
      setLoading(false);
      return;
    }
    if (!silent) {
      setLoading(true);
      setLoadError('');
    }
    try {
      const r = await getSDK().vpn.listVpnProfiles({ activeOnly: false });
      setProfiles((r.items || []).map((p) => ({
        id: p.id, code: p.code, name: p.name, synonym: p.synonym, isActive: !!p.isActive,
      })));
    } catch (e) {
      log.error('load vpn connections', { error: e });
      // Фоновое обновление по шине не должно затирать уже показанный список
      // и подменять его ошибкой — ошибка только для явной загрузки.
      if (!silent) {
        setLoadError(vpnErrorMessage(e, 'list'));
        setProfiles([]);
      }
    } finally {
      setLoading(false);
    }
  }, [canUse]);

  useEffect(() => { void loadProfiles(); }, [loadProfiles]);

  // ЖИВОЕ ОБНОВЛЕНИЕ: профиль создан/изменён/удалён или изменены доступы
  // (шина) — список доступных подключений перечитывается без перезагрузки.
  useEffect(() => {
    const unsub = eventBusHub.subscribe((ev) => {
      const topic = ev.topic || '';
      if (topic.startsWith('vpn.profile.') || topic === 'vpn.access.updated') void loadProfiles(true);
    });
    return unsub;
  }, [loadProfiles]);

  // Выбранное подключение: явный выбор пользователя, иначе первое доступное
  // (если профиль удалён/закрыт — автоматически первое, без эффекта-сброса).
  const selected = useMemo(
    () => profiles.find((p) => p.code === selectedCode) || profiles[0] || null,
    [profiles, selectedCode],
  );
  const activeCode = selected?.code || '';

  const connect = useCallback(async (profile: VpnConnectionProfile): Promise<boolean> => {
    if (profile.isActive === false) return false;
    setBusyCode(profile.code);
    try {
      // Сначала убеждаемся, что настройки доступны (права + конфигурация),
      // и только затем открываем сессию в журнале.
      await getSDK().vpn.getVpnConfig({ profileCode: profile.code });
      const r = await getSDK().vpn.startVpnSession({
        profileCode: profile.code,
        clientInfo: clientInfo(),
      });
      if (!r.sessionId) throw new Error('Сервер не подтвердил подключение. Повторите попытку.');
      setSessions((prev) => ({ ...prev, [profile.code]: r.sessionId }));
      showSuccess(`Подключение «${vpnConnectionLabel(profile)}» открыто`);
      return true;
    } catch (e) {
      log.error('start vpn session', { error: e, code: profile.code });
      showError(vpnErrorMessage(e, 'connect'));
      return false;
    } finally {
      setBusyCode('');
    }
  }, []);

  const disconnect = useCallback(async (profile: VpnConnectionProfile): Promise<boolean> => {
    const sessionId = sessions[profile.code];
    if (!sessionId) {
      setSessions((prev) => {
        const next = { ...prev };
        delete next[profile.code];
        return next;
      });
      return true;
    }
    setBusyCode(profile.code);
    try {
      await getSDK().vpn.finishVpnSession({ sessionId, status: 'disconnected' });
      setSessions((prev) => {
        const next = { ...prev };
        delete next[profile.code];
        return next;
      });
      showSuccess(`Подключение «${vpnConnectionLabel(profile)}» закрыто`);
      return true;
    } catch (e) {
      log.error('finish vpn session', { error: e, code: profile.code });
      showError(vpnErrorMessage(e, 'disconnect'));
      return false;
    } finally {
      setBusyCode('');
    }
  }, [sessions]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3 shrink-0">
        <div className="min-w-0">
          <div className="text-sm font-medium">Ваши подключения</div>
        </div>
        <Button variant="outline" size="sm" onClick={() => void loadProfiles()} disabled={loading || !canUse}>
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Обновить
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-3">
        {!canUse && (
          <div className="mx-auto max-w-md rounded-lg border bg-card p-5 text-center">
            <ShieldOff className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
            <div className="text-sm font-medium">VPN недоступен для вашей учётной записи</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Попросите администратора открыть вам доступ к VPN — после этого здесь появятся
              доступные подключения.
            </div>
          </div>
        )}

        {canUse && loading && (
          <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
            Загружаем доступные подключения…
          </div>
        )}

        {canUse && !loading && loadError && (
          <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
            <div className="text-destructive">{loadError}</div>
            <Button variant="outline" size="sm" onClick={() => void loadProfiles()}>Повторить</Button>
          </div>
        )}

        {canUse && !loading && !loadError && profiles.length === 0 && (
          <div className="rounded-lg border bg-card p-5 text-center">
            <div className="text-sm font-medium">Пока нет доступных подключений</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {canManage
                ? 'Создайте подключение и откройте к нему доступ в режиме «Управление».'
                : 'Обратитесь к администратору — он настроит подключение и откроет вам доступ.'}
            </div>
          </div>
        )}

        {canUse && !loading && !loadError && profiles.length > 0 && (
          <div className="grid gap-3 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
            <div className="space-y-2">
              {profiles.map((p) => (
                <VpnConnectionCard
                  key={p.code}
                  profile={p}
                  selected={p.code === activeCode}
                  connected={!!sessions[p.code]}
                  onSelect={() => setSelectedCode(p.code)}
                />
              ))}
            </div>
            <div className="min-h-[320px]">
              {selected && (
                <VpnConnectPanel
                  key={selected.code}
                  profile={selected}
                  connected={!!sessions[selected.code]}
                  busy={busyCode === selected.code}
                  onConnect={() => connect(selected)}
                  onDisconnect={() => disconnect(selected)}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
